import React, { useState } from 'react';
import { processLiveStreamPurchase } from '@/services/liveStreamService';
import { Product, Stream } from '@/types/liveStreamTypes';
import { useToast } from '@/components/ui/use-toast';
import { Loader } from '@/components/shared';
import { liveStreamCreateDealStatus } from '@/lib/appwrite/api';
import { notificationService } from '@/services/NotificationService';
import { useNavigate } from 'react-router-dom';
import { createStreamPurchaseOrder, verifyCashfreePayment } from '@/lib/cashfree/paymentService';
import { X, Shield, Wallet,  CreditCard, Gift, Zap } from 'lucide-react';
import { getUserTrustScore, logTrustScoreEvent } from '@/lib/appwrite/trustScore';
import { updateScheduledDiscountUsage } from '@/services/liveStreamService';

interface LiveStreamPurchaseProps {
  stream: Stream;
  product: Product;
  user: {
    $id: string;
    name: string;
    email?: string;
    phone?: string;
    trustScore?: number;
  };
  onSuccess?: (data: {
    productId: string;
    orderId: string;
    paymentId: string;
  }) => void;
}

interface PaymentBreakdown {
  originalAmount: number;
  trustDiscount: number;
  scheduledDiscount: number;
  finalDiscount: number;
  discountType: 'trust' | 'scheduled' | 'both' | 'none';
  platformFee: number;
  platformFeePercent: number;
  finalAmount: number;
  canApplyDiscount: boolean;
  scheduledDiscountAvailable: boolean;
  scheduledDiscountPercent: number;
  scheduledDiscountRemaining: number;
  trustScorePoints: number;
  maxTrustDiscountAvailable: number;
}

declare global {
  interface Window {
    Cashfree: any;
  }
}

const LiveStreamPurchase: React.FC<LiveStreamPurchaseProps> = ({ 
  stream, 
  product, 
  user, 
  onSuccess 
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentBreakdown, setPaymentBreakdown] = useState<PaymentBreakdown | null>(null);
  const [applyDiscount, setApplyDiscount] = useState(true);
  const quantity = product.quantity || parseInt(product.minQuantity) || 1;
  const { toast } = useToast();
  const navigate = useNavigate();

  // Combined loading state
  const isPaymentLoading = isProcessing || isVerifying;

  const calculatePaymentBreakdown = async (buyerTrustScore: number): Promise<PaymentBreakdown> => {
    const unitPrice = parseFloat(product.price);
    const productQuantity = product.quantity || 1;
    const originalAmount = unitPrice * productQuantity;

    // Get platform fee percentage based on trust score
    const getPlatformFeePercent = (trustScore: number): number => {
      if (trustScore >= 5000) return 1;
      if (trustScore >= 3500) return 1.5;
      if (trustScore >= 2000) return 2;
      if (trustScore >= 1000) return 2.5;
      return 3;
    };

    const platformFeePercent = getPlatformFeePercent(buyerTrustScore);
    const platformFee = (originalAmount * platformFeePercent) / 100;

    // Calculate trust score discount - FIXED LOGIC
    const maxTrustDiscountAvailable = buyerTrustScore / 10; // Convert trust score to rupees
    const potentialTrustDiscountPercent = 10; // 10% of product price
    const potentialTrustDiscountAmount = (originalAmount * potentialTrustDiscountPercent) / 100;

    // Apply the minimum of: available trust score discount OR 10% of product price
    const trustDiscount = Math.min(maxTrustDiscountAvailable, potentialTrustDiscountAmount);

    // Calculate scheduled discount
    let scheduledDiscount = 0;
    let scheduledDiscountAvailable = false;
    let scheduledDiscountRemaining = 0;
    let scheduledDiscountPercent = 0;

    if (stream.scheduledDiscountPercent && stream.scheduledDiscountMaxUsers) {
      scheduledDiscountPercent = parseInt(stream.scheduledDiscountPercent);
      const maxUsers = parseInt(stream.scheduledDiscountMaxUsers);
      const usedCount = parseInt(stream.scheduledDiscountUsedCount || '0');
      scheduledDiscountRemaining = maxUsers - usedCount;

      if (scheduledDiscountRemaining > 0) {
        scheduledDiscountAvailable = true;
        scheduledDiscount = (originalAmount * scheduledDiscountPercent) / 100;
      }
    }

    // Both discounts can apply together
    const totalDiscount = trustDiscount + scheduledDiscount;

    // Determine discount type
    let discountType: 'scheduled' | 'trust' | 'both' | 'none' = 'none';
    if (scheduledDiscount > 0 && trustDiscount > 0) {
      discountType = 'both';
    } else if (scheduledDiscount > 0) {
      discountType = 'scheduled';
    } else if (trustDiscount > 0) {
      discountType = 'trust';
    }

    // Calculate final amount (apply discount only if applyDiscount is true)
    const subtotal = originalAmount + platformFee;
    const finalAmount = applyDiscount ? subtotal - totalDiscount : subtotal;

    return {
      originalAmount,
      trustDiscount,
      scheduledDiscount,
      finalDiscount: totalDiscount,
      discountType,
      platformFee,
      platformFeePercent,
      finalAmount,
      canApplyDiscount: totalDiscount > 0,
      scheduledDiscountAvailable,
      scheduledDiscountPercent,
      scheduledDiscountRemaining,
      trustScorePoints: buyerTrustScore,
      maxTrustDiscountAvailable
    };
  };

  const handleBuyNowClick = async () => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login to make purchases",
        variant: "destructive"
      });
      return;
    }

    try {
      const buyerTrustData = await getUserTrustScore(user.$id);
      const breakdown = await calculatePaymentBreakdown(buyerTrustData?.score || 0);

      setPaymentBreakdown(breakdown);
      setShowPaymentDialog(true);

      // Always apply discount if available
      setApplyDiscount(true);
    } catch (error) {
      console.error("Error calculating payment breakdown:", error);
      toast({
        title: "Error",
        description: "Could not calculate payment details. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleConfirmPayment = async () => {
    if (!paymentBreakdown) return;

    if (!window.Cashfree) {
      toast({
        title: "Payment System Error",
        description: "Payment system is not ready. Please refresh the page.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);

    try {
      // Step 1: Create order in our system
      const { order } = await processLiveStreamPurchase({
        streamId: stream.$id,
        productId: product.$id,
        buyerId: user.$id,
        buyerName: user.name,
        quantity: quantity
      });

      // Calculate buyer's final payment amount (use paymentBreakdown.finalAmount which already considers applyDiscount)
      const buyerPaymentAmount = paymentBreakdown.finalAmount;

      // Step 2: Initialize Cashfree payment
      const paymentSession = await createStreamPurchaseOrder(
        stream.$id,
        product.$id,
        buyerPaymentAmount,
        {
          id: user.$id,
          name: user.name,
          email: user.email,
          phone: user.phone
        },
        product.sellerId,
        product.name,
        `${window.location.origin}/payment/callback?order_id=${order.cashfreeOrderId}&source=stream&entity_id=${stream.$id}&buyer_id=${user.$id}`
      );

      if (!paymentSession.success || !paymentSession.payment_session_id) {
        throw new Error(paymentSession.error || 'Failed to create payment session');
      }

      const cashfreeOrderId = paymentSession.order_id;

      // Step 3: Initialize Cashfree checkout
      const cashfree = new window.Cashfree({
        mode: 'production'
      });

      const checkoutOptions = {
        paymentSessionId: paymentSession.payment_session_id,
        returnUrl: `https://busiconn.com/payment/callback?order_id=${cashfreeOrderId}&source=stream&entity_id=${stream.$id}&buyer_id=${user.$id}`,
        redirectTarget: '_modal'
      };

      setShowPaymentDialog(false);
      setIsProcessing(false); // Reset here so modal can open

      // Handle payment completion
      cashfree.checkout(checkoutOptions).then(async (response: any) => {
        setIsVerifying(true); // Start verification phase
        
        try {
          if (response.error) {
            throw new Error(response.error.message || 'Payment failed');
          }

          if (response.paymentDetails) {
            const verificationOrderId = cashfreeOrderId;

            const isValid = await verifyCashfreePayment({
              orderId: verificationOrderId ?? "",
              paymentId: response.paymentDetails.paymentId,
            });

            if (!isValid) {
              throw new Error("Payment verification failed");
            }

            if (!cashfreeOrderId) {
              console.error('Missing orderId!');
              return;
            }

            // Create escrow deal status
            const dealResult = await liveStreamCreateDealStatus({
              buyerId: user.$id,
              sellerId: product.sellerId,
              amount: paymentBreakdown.originalAmount.toString(),
              cashfreeOrderId: cashfreeOrderId,
              cashfreePaymentId: response.paymentDetails.paymentId,
              paymentVerifiedAt: new Date().toISOString(),
              wasInitiated: true
            });

            // Handle discount usage (only if discount was applied)
            if (applyDiscount && paymentBreakdown.finalDiscount > 0) {
              if (paymentBreakdown.discountType === 'scheduled' || paymentBreakdown.discountType === 'both') {
                // Update scheduled discount usage count
                try {
                  await updateScheduledDiscountUsage(stream.$id);
                } catch (error) {
                  console.error("Failed to update scheduled discount usage:", error);
                }
              }

              if (paymentBreakdown.discountType === 'trust' || paymentBreakdown.discountType === 'both') {
                // Log trust score discount usage - FIXED: Deduct actual trust discount amount
                try {
                  await logTrustScoreEvent(
                    user.$id,
                    'discount_used',
                    -Math.round(paymentBreakdown.trustDiscount * 10), // Convert back to trust score points
                    `Trust score discount of ₹${paymentBreakdown.trustDiscount.toFixed(2)} used for ${product.title}`,
                    dealResult?.$id || cashfreeOrderId
                  );

                  await logTrustScoreEvent(
                    user.$id,
                    'purchase_points',
                    10,
                    'Trustscore pts awarded for purchasing, cancellation will deduct it.',
                    dealResult?.$id || cashfreeOrderId
                  );
                } catch (trustScoreError) {
                  console.error("Failed to update trust score:", trustScoreError);
                }
              }
            }

            // Notify seller
            await notificationService.createNotification({
              title: "Payment Received",
              body: `₹${paymentBreakdown.originalAmount} secured in escrow`,
              type: "payment",
              recipient_id: product.sellerId,
              sender_id: user.$id,
            });

            onSuccess?.({
              productId: product.$id,
              orderId: cashfreeOrderId,
              paymentId: response.paymentDetails.paymentId
            });

            const discountMessage = paymentBreakdown.finalDiscount > 0 ? 
              ` (Saved ₹${paymentBreakdown.finalDiscount.toFixed(2)})` : '';

            toast({
              title: "Payment Successful! 🎉",
              description: `₹${buyerPaymentAmount.toFixed(2)} paid successfully${discountMessage}`,
            });

            navigate(`/chats/${product.sellerId}`);
          }
        } catch (error) {
          console.error("Payment processing failed:", error);

          await notificationService.createNotification({
            title: "Payment Error",
            body: "Contact support for assistance",
            type: "error",
            recipient_id: user.$id,
            sender_id: "system",
          });

          toast({
            title: "Payment Verification Failed",
            description: "Our team will review your payment shortly",
            variant: "destructive"
          });
        } finally {
          setIsVerifying(false);
        }
      }).catch((error: any) => {
        console.error("Cashfree checkout error:", error);
        toast({
          title: "Payment Failed",
          description: error.message || "Payment could not be completed",
          variant: "destructive"
        });
        setIsVerifying(false);
      });

    } catch (error) {
      console.error("Purchase error:", error);
      toast({
        title: "Purchase Failed",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive"
      });
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* Payment Loader Overlay - ONLY ADDITION */}
      {isPaymentLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center max-w-sm mx-4">
            <Loader />
            <p className="mt-4 text-gray-700 font-medium text-center">
              {isProcessing ? 'Processing Payment...' : 'Verifying Payment...'}
            </p>
            <p className="mt-2 text-sm text-gray-500 text-center">
              {isProcessing ? 'Initializing payment gateway' : 'Please wait, completing your purchase'}
            </p>
            <p className="mt-1 text-xs text-gray-400 text-center">
              Please don't close this window
            </p>
          </div>
        </div>
      )}

      <div className="mb-12">       
        <button
          onClick={handleBuyNowClick}
          disabled={isPaymentLoading}
          className={`w-full py-3 px-4 rounded-xl font-semibold text-lg ${
            isPaymentLoading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700"
          } text-white transition-all duration-300 transform hover:scale-[1.02] shadow-lg disabled:transform-none`}
        >
          {isPaymentLoading ? (
            <div className="flex items-center justify-center">
              <Loader />
              <span className="ml-2">
                {isProcessing ? 'Processing...' : 'Verifying...'}
              </span>
            </div>
          ) : (
            `Buy Now - ₹${(Number(product.price) * (product.quantity || 1)).toFixed(2)}`
          )}
        </button>
        <p className="mt-3 text-xs text-gray-500 text-center flex items-center justify-center">
          <Shield size={12} className="mr-1" />
          Payment secured by Cashfree Escrow
        </p>
      </div>

      {/* Enhanced Payment Breakdown Dialog */}
      {showPaymentDialog && paymentBreakdown && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Payment Details</h2>
                <p className="text-sm text-gray-500 mt-1">Review your purchase</p>
              </div>
              <button
                onClick={() => setShowPaymentDialog(false)}
                disabled={isPaymentLoading}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Product Info */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-1">{product.title}</h3>
                <p className="text-sm text-gray-600">From {stream.title}</p>
              </div>

              {/* Scheduled Discount Banner */}
              {paymentBreakdown.scheduledDiscountAvailable && (
                <div className="bg-gradient-to-r from-orange-500 to-rose-500 text-white rounded-xl p-4 relative overflow-hidden">
                  <div className="flex items-center justify-between relative z-10">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mr-3">
                        🎉
                      </div>
                      <div>
                        <div className="font-bold text-lg">Early Bird Special!</div>
                        <div className="text-sm opacity-90">
                          {paymentBreakdown.scheduledDiscountPercent}% OFF for first buyers
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-lg">{paymentBreakdown.scheduledDiscountRemaining}</div>
                      <div className="text-xs opacity-75">spots left</div>
                    </div>
                  </div>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                  <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/10 rounded-full translate-y-10 -translate-x-10"></div>
                </div>
              )}

              {/* Payment Breakdown */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 flex items-center text-lg">
                  <CreditCard className="w-5 h-5 mr-2 text-blue-600" />
                  Payment Breakdown
                </h4>

                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Product Price</span>
                    <span className="font-semibold text-gray-900">₹{paymentBreakdown.originalAmount.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 flex items-center">
                      <Shield className="w-4 h-4 mr-1 text-green-600" />
                      Platform Fee ({paymentBreakdown.platformFeePercent}%)
                    </span>
                    <span className="font-semibold text-green-600">₹{paymentBreakdown.platformFee.toFixed(2)}</span>
                  </div>

                  <div className="border-t pt-3">
                    <div className="flex justify-between items-center font-semibold">
                      <span>Subtotal</span>
                      <span>₹{(paymentBreakdown.originalAmount + paymentBreakdown.platformFee).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Applied Discounts */}
                {paymentBreakdown.canApplyDiscount && (
                  <div className="space-y-4">
                    <h5 className="font-medium text-gray-700">Applied Discounts</h5>

                    {/* Scheduled Discount */}
                    {paymentBreakdown.scheduledDiscountAvailable && paymentBreakdown.scheduledDiscount > 0 && (
                      <div className="border-2 border-orange-400 bg-orange-50 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Gift className="w-4 h-4 text-orange-600 mr-2" />
                            <span className="font-semibold text-orange-700">Early Bird Discount</span>
                          </div>
                          <span className="font-bold text-orange-600">
                            -₹{paymentBreakdown.scheduledDiscount.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-sm text-orange-600 mt-1">
                          Limited time: {paymentBreakdown.scheduledDiscountPercent}% off for first {paymentBreakdown.scheduledDiscountRemaining} buyers
                        </p>
                      </div>
                    )}

                    {/* Trust Score Discount */}
                    {paymentBreakdown.trustDiscount > 0 && (
                      <div className="border-2 border-blue-400 bg-blue-50 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Wallet className="w-4 h-4 text-blue-600 mr-2" />
                            <span className="font-semibold text-blue-700">Trust Score Discount</span>
                          </div>
                          <span className="font-bold text-blue-600">
                            -₹{paymentBreakdown.trustDiscount.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-sm text-blue-600 mt-1">
                          10% discount cap applied from your trust score balance (₹{paymentBreakdown.maxTrustDiscountAvailable.toFixed(2)} available)
                        </p>
                      </div>
                    )}

                    {/* Total Savings */}
                    {paymentBreakdown.finalDiscount > 0 && (
                      <div className="bg-green-50 rounded-xl p-3 border border-green-200">
                        <div className="flex justify-between items-center">
                          <span className="text-green-700 font-medium">Total Savings</span>
                          <span className="font-bold text-green-700">₹{paymentBreakdown.finalDiscount.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Final Total */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border-2 border-blue-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Total Amount</span>
                    <div className="text-right">
                      {paymentBreakdown.finalDiscount > 0 && (
                        <div className="text-sm text-gray-500 line-through">
                          ₹{(paymentBreakdown.originalAmount + paymentBreakdown.platformFee).toFixed(2)}
                        </div>
                      )}
                      <div className="text-2xl font-bold text-gray-900">
                        ₹{paymentBreakdown.finalAmount.toFixed(2)}
                      </div>
                      {paymentBreakdown.finalDiscount > 0 && (
                        <div className="text-sm font-medium text-green-600">
                          You save ₹{paymentBreakdown.finalDiscount.toFixed(2)}!
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Trust Score Info */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border border-yellow-200">
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center mr-3">
                    <Zap className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-semibold text-yellow-800">Trust Score Benefits</span>
                </div>
                <p className="text-sm text-yellow-700">
                  Your trust score: {paymentBreakdown.trustScorePoints || 0} | Available discount: ₹{paymentBreakdown.maxTrustDiscountAvailable.toFixed(2)}
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  Platform fee: {paymentBreakdown.platformFeePercent}% | Higher trust scores = lower fees!
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleConfirmPayment}
                  disabled={isPaymentLoading}
                  className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                >
                  {isPaymentLoading ? (
                    <>
                      <Loader />
                      <span className="ml-2">
                        {isProcessing ? 'Processing Payment...' : 'Verifying Payment...'}
                      </span>
                    </>
                  ) : (
                    <>
                      <Shield className="w-5 h-5 mr-2" />
                      Confirm & Pay ₹{paymentBreakdown.finalAmount.toFixed(2)}
                    </>
                  )}
                </button>

                <button
                  onClick={() => setShowPaymentDialog(false)}
                  disabled={isPaymentLoading}
                  className="w-full py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>

              {/* Security Note */}
              <div className="text-center">
                <p className="text-xs text-gray-500 flex items-center justify-center">
                  <Shield size={12} className="mr-1 text-green-600" />
                  256-bit SSL encryption • PCI DSS compliant
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LiveStreamPurchase;